import { Controller } from "@hotwired/stimulus"

/**
 * Collapsible pipeline panels.
 * Open/closed state persists per section in localStorage.
 */
export default class extends Controller {
  static targets = ["body", "toggle"]

  static values = {
    section: { type: String, default: "panel" },
    open: { type: Boolean, default: true },
    storageKey: { type: String, default: "pri2m-cd-collapse" }
  }

  connect() {
    const stored = this.readStored()
    if (stored !== null) {
      this.openValue = stored
    }
    this.apply({ animate: false })
  }

  toggle(event) {
    event?.preventDefault?.()
    this.openValue = !this.openValue
    this.writeStored(this.openValue)
    this.apply({ animate: true })
  }

  apply({ animate = false } = {}) {
    const open = this.openValue

    this.element.classList.toggle("is-collapsed", !open)

    if (this.hasBodyTarget) {
      this.bodyTarget.hidden = !open
      this.bodyTarget.classList.toggle("is-collapsed-body", !open)
      if (animate && open) {
        this.bodyTarget.classList.add("cd-body-enter")
        window.setTimeout(() => this.bodyTarget.classList.remove("cd-body-enter"), 220)
      }
    }

    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute("aria-expanded", open ? "true" : "false")
      const chevron = this.toggleTarget.querySelector(".cd-collapse-chevron")
      if (chevron) chevron.textContent = open ? "▾" : "▸"
    }

    // Let nested tables (DataTables) remeasure after the body is shown.
    if (open) {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(
          new CustomEvent("cd:panel-opened", {
            detail: { section: this.sectionValue, element: this.element }
          })
        )
      })
    }
  }

  storageFullKey() {
    return `${this.storageKeyValue}:${this.sectionValue}`
  }

  readStored() {
    try {
      const raw = window.localStorage.getItem(this.storageFullKey())
      if (raw === "0") return false
      if (raw === "1") return true
      return null
    } catch (_error) {
      return null
    }
  }

  writeStored(open) {
    try {
      window.localStorage.setItem(this.storageFullKey(), open ? "1" : "0")
    } catch (_error) {
      // private mode / blocked storage
    }
  }
}
